// Content API (Homepage, Blog, Site Policies)
import { apiRequest } from './request.js';
import { withCache, CACHE_STORE, invalidateCache } from './config.js';

// Content API
export const contentAPI = {
    // Get content section (public) - with caching for better performance
    getSection: async (section, key = null, forceRefresh = false) => {
        const endpoint = key ? `/content/${section}/?key=${key}` : `/content/${section}/`;
        const cacheKey = `content_section_${section}_${key || 'all'}`;

        // Use cache for frequently accessed content sections
        if (!forceRefresh) {
            return withCache(cacheKey, () => apiRequest(endpoint, {
                requireAuth: false
            }), {
                ttl: 24 * 60 * 60 * 1000, // 24 hours (with persistent storage)
                persist: true
            });
        }

        // Force refresh - clear cache and fetch fresh
        invalidateCache(cacheKey);
        return apiRequest(endpoint, {
            requireAuth: false
        });
    },

    // Get all homepage content in a single request (for faster homepage loading)
    getHomepageAll: async (forceRefresh = false) => {
        const cacheKey = 'content_homepage_all';

        if (!forceRefresh) {
            return withCache(cacheKey, () => apiRequest('/content/homepage-all/', {
                requireAuth: false
            }), {
                ttl: 24 * 60 * 60 * 1000, // 24 hours
                persist: true
            });
        }

        invalidateCache(cacheKey);
        return apiRequest('/content/homepage-all/', {
            requireAuth: false
        });
    },

    // Update content (admin only)
    updateContent: async (section, key, content, contentType = 'text') => {
        const result = await apiRequest(`/content/${section}/${key}/`, {
            method: 'PUT',
            body: JSON.stringify({ content, contentType })
        });
        // Invalidate AFTER successful save so cache reflects new data
        invalidateCache('content_section_*');
        invalidateCache('content_homepage_all');
        return result;
    },

    // ========================================================
    // DEPRECATED: Blog article endpoints (Django /api/blog/)
    // Migrado a strapiBlogAPI (CMS /api/cms/blog/) en strapi.js
    // Se mantienen temporalmente por retrocompatibilidad.
    // ========================================================

    // @deprecated Use strapiBlogAPI.listArticles() instead
    listArticles: async (filters = {}) => {
        console.warn('[DEPRECATED] contentAPI.listArticles() → usar strapiBlogAPI.listArticles()');
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = queryParams ? `/blog/?${queryParams}` : '/blog/';
        return apiRequest(endpoint, { requireAuth: true });
    },

    // @deprecated Use strapiBlogAPI.getArticle(id) instead
    getArticleById: async (id) => {
        console.warn('[DEPRECATED] contentAPI.getArticleById() → usar strapiBlogAPI.getArticle()');
        return apiRequest(`/blog/${id}/`, { requireAuth: false });
    },

    // @deprecated Use strapiBlogAPI.getArticle(slug) instead
    getArticleBySlug: async (slug) => {
        console.warn('[DEPRECATED] contentAPI.getArticleBySlug() → usar strapiBlogAPI.getArticle(slug)');
        return apiRequest(`/blog/?search=${encodeURIComponent(slug)}`, { requireAuth: false });
    },

    // @deprecated Use strapiBlogAPI.listArticles() with filters
    getArticlesByBusiness: async (businessId) => {
        console.warn('[DEPRECATED] contentAPI.getArticlesByBusiness()');
        return apiRequest(`/blog/?business=${businessId}`, { requireAuth: false });
    },

    // @deprecated Use strapiBlogAPI.createArticle() instead
    createArticle: async (article) => {
        console.warn('[DEPRECATED] contentAPI.createArticle() → usar strapiBlogAPI.createArticle()');
        return apiRequest('/blog/', {
            method: 'POST',
            body: JSON.stringify(article)
        });
    },

    // @deprecated Use strapiBlogAPI.updateArticle() instead
    updateArticle: async (id, article) => {
        console.warn('[DEPRECATED] contentAPI.updateArticle() → usar strapiBlogAPI.updateArticle()');
        return apiRequest(`/blog/${id}/`, {
            method: 'PATCH',
            body: JSON.stringify(article)
        });
    },

    // @deprecated Use strapiBlogAPI.deleteArticle() instead
    deleteArticle: async (id) => {
        console.warn('[DEPRECATED] contentAPI.deleteArticle() → usar strapiBlogAPI.deleteArticle()');
        return apiRequest(`/blog/${id}/`, {
            method: 'DELETE'
        });
    },

    // @deprecated No equivalent in CMS - status managed via updateArticle
    publishArticle: async (id) => {
        console.warn('[DEPRECATED] contentAPI.publishArticle() - usar strapiBlogAPI.updateArticle() con status');
        return apiRequest(`/blog/${id}/publish/`, {
            method: 'POST'
        });
    },

    // @deprecated No equivalent in CMS
    generateUniqueSlug: async (title, excludeId = null) => {
        console.warn('[DEPRECATED] contentAPI.generateUniqueSlug()');
        return apiRequest('/blog/generate-slug/', {
            method: 'POST',
            body: JSON.stringify({ title, excludeId })
        });
    },

    // @deprecated CMS auto-increments views on GET /api/cms/blog/<slug>/
    trackArticleView: async (articleId) => {
        console.warn('[DEPRECATED] contentAPI.trackArticleView() - CMS incrementa views automaticamente');
        return apiRequest(`/blog/${articleId}/view/`, {
            method: 'POST',
            requireAuth: false
        });
    },

    // @deprecated Use trackEvent('share_article', ...) from utils/tracking.js
    trackArticleShare: async (articleId, shareType = 'native') => {
        console.warn('[DEPRECATED] contentAPI.trackArticleShare() - usar trackEvent()');
        return apiRequest(`/blog/${articleId}/share/`, {
            method: 'POST',
            body: JSON.stringify({ shareType }),
            requireAuth: false
        });
    },

    // @deprecated Comments not available in CMS
    addArticleComment: async (articleId, commentData) => {
        console.warn('[DEPRECATED] contentAPI.addArticleComment() - comentarios no disponibles en CMS');
        return apiRequest(`/blog/${articleId}/comment/`, {
            method: 'POST',
            body: JSON.stringify(commentData),
            requireAuth: false
        });
    },

    // @deprecated Not available in CMS - use categories from article list
    getPopularCategories: async () => {
        console.warn('[DEPRECATED] contentAPI.getPopularCategories() - no disponible en CMS');
        return apiRequest('/blog/popular-categories/', { requireAuth: false });
    },

    // @deprecated Use strapiBlogAPI.listArticles() and sort client-side
    getPopularArticles: async (limit = 6) => {
        console.warn('[DEPRECATED] contentAPI.getPopularArticles() → usar strapiBlogAPI.listArticles()');
        return apiRequest(`/blog/?ordering=-published_at&page_size=${limit}`, { requireAuth: false });
    }
};

// FAQ API
export const faqAPI = {
    getAll: async () => {
        const data = await apiRequest('/faq/');
        // Handle both plain array and DRF paginated response { results: [], count: X }
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.results)) return data.results;
        return [];
    },
    create: async (faq) => {
        return apiRequest('/faq/', {
            method: 'POST',
            body: JSON.stringify(faq)
        });
    },
    update: async (id, faq) => {
        return apiRequest(`/faq/${id}/`, {
            method: 'PUT',
            body: JSON.stringify(faq)
        });
    },
    delete: async (id) => {
        return apiRequest(`/faq/${id}/`, {
            method: 'DELETE'
        });
    }
};

// Site Policies API - with caching for better performance
const sitePolicyCache = {
    all: null,
    bySlug: {},
    timestamp: null,
    maxAge: 5 * 60 * 1000 // 5 minutes cache
};

export const sitePolicyAPI = {
    getAll: async (forceRefresh = false) => {
        // Return cached data if available and not expired
        const now = Date.now();
        if (!forceRefresh && sitePolicyCache.all && sitePolicyCache.timestamp && (now - sitePolicyCache.timestamp < sitePolicyCache.maxAge)) {
            return sitePolicyCache.all;
        }

        const data = await apiRequest('/site-policies/');
        sitePolicyCache.all = data;
        sitePolicyCache.timestamp = now;

        // Pre-populate bySlug cache
        if (Array.isArray(data)) {
            data.forEach(policy => {
                if (policy.slug) {
                    sitePolicyCache.bySlug[policy.slug] = policy;
                }
            });
        }

        return data;
    },

    getBySlug: async (slug) => {
        // Check cache first
        if (sitePolicyCache.bySlug[slug]) {
            return sitePolicyCache.bySlug[slug];
        }

        // If we have all policies cached, try to find it there
        if (sitePolicyCache.all && Array.isArray(sitePolicyCache.all)) {
            const cached = sitePolicyCache.all.find(p => p.slug === slug);
            if (cached) {
                sitePolicyCache.bySlug[slug] = cached;
                return cached;
            }
        }

        // Fetch from API
        const data = await apiRequest(`/site-policies/by-slug/${slug}/`);
        sitePolicyCache.bySlug[slug] = data;
        return data;
    },

    getActive: async (type) => {
        return apiRequest(`/site-policies/active/${type}/`);
    },

    create: async (data) => {
        // Invalidate cache on create
        sitePolicyCache.all = null;
        sitePolicyCache.timestamp = null;
        return apiRequest('/site-policies/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    update: async (id, data) => {
        // Invalidate cache on update
        sitePolicyCache.all = null;
        sitePolicyCache.timestamp = null;
        sitePolicyCache.bySlug = {};
        return apiRequest(`/site-policies/${id}/`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },

    delete: async (id) => {
        // Invalidate cache on delete
        sitePolicyCache.all = null;
        sitePolicyCache.timestamp = null;
        sitePolicyCache.bySlug = {};
        return apiRequest(`/site-policies/${id}/`, {
            method: 'DELETE'
        });
    },

    // Clear cache manually if needed
    clearCache: () => {
        sitePolicyCache.all = null;
        sitePolicyCache.timestamp = null;
        sitePolicyCache.bySlug = {};
    },

    // Admin: get ALL documents including inactive
    getAllAdmin: async () => {
        return apiRequest('/legal/all/');
    },

    // Admin: update document via dedicated admin endpoint
    updateDocument: async (id, data) => {
        // Invalidate cache
        sitePolicyCache.all = null;
        sitePolicyCache.timestamp = null;
        sitePolicyCache.bySlug = {};
        return apiRequest(`/legal/${id}/update_document/`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }
};

// ========================================================
// DEPRECATED: Newsletter API (Django /api/newsletter/)
// Migrado a strapiNewsletterAPI (CMS /api/cms/newsletter/) en strapi.js
// Se mantiene temporalmente por retrocompatibilidad.
// ========================================================
export const newsletterAPI = {
    // Send newsletter to all active subscribers (admin only)
    send: async ({ subject, message, html_message }) => {
        const body = { subject, message };
        if (html_message) body.html_message = html_message;
        return apiRequest('/newsletter/send/', {
            method: 'POST',
            body: JSON.stringify(body)
        });
    },

    // @deprecated Use strapiNewsletterAPI.subscribe() instead
    subscribe: async (email) => {
        console.warn('[DEPRECATED] newsletterAPI.subscribe() → usar strapiNewsletterAPI.subscribe()');
        return apiRequest('/newsletter/subscribe/', {
            method: 'POST',
            body: JSON.stringify({ email }),
            requireAuth: false
        });
    },

    // @deprecated Use strapiNewsletterAPI.unsubscribe() instead
    unsubscribe: async (email) => {
        console.warn('[DEPRECATED] newsletterAPI.unsubscribe() → usar strapiNewsletterAPI.unsubscribe()');
        return apiRequest('/newsletter/unsubscribe/', {
            method: 'POST',
            body: JSON.stringify({ email }),
            requireAuth: false
        });
    },

    // @deprecated Use strapiNewsletterAPI.listSubscriptions() instead
    getAll: async () => {
        console.warn('[DEPRECATED] newsletterAPI.getAll() → usar strapiNewsletterAPI.listSubscriptions()');
        return apiRequest('/newsletter/');
    }
};
